import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { FileText, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SkeletonRows } from "@/components/ui/skeleton";

export function DocumentsTab({ patientId }: { patientId: string }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const documents = useQuery({
    queryKey: ["documents", patientId],
    queryFn: () => api.patients.documents(patientId),
  });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return api.patients.uploadDocument(patientId, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", patientId] });
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
    },
  });

  const remove = useMutation({
    mutationFn: (docId: string) => api.patients.deleteDocument(patientId, docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents", patientId] }),
  });

  if (documents.isLoading) return <SkeletonRows rows={3} />;

  return (
    <div className="max-w-2xl">
      <div className="mb-4 flex justify-end">
        <input
          ref={fileInput}
          type="file"
          hidden
          accept="image/*,application/pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            e.target.value = "";
          }}
        />
        <Button onClick={() => fileInput.current?.click()} loading={upload.isPending}>
          <Upload size={15} /> Upload document
        </Button>
      </div>
      {upload.isError && (
        <p className="mb-3 text-sm text-danger">
          {upload.error instanceof Error ? upload.error.message : "Upload failed"}
        </p>
      )}

      {(documents.data ?? []).length === 0 ? (
        <Card className="py-10 text-center text-sm text-text-tertiary">
          No documents uploaded — referral letters, scans and images live here.
        </Card>
      ) : (
        <div className="space-y-2">
          {(documents.data ?? []).map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 rounded-lg border bg-bg-secondary px-4 py-3 transition hover:bg-bg-tertiary"
              style={{ borderColor: "var(--accent-subtle)" }}
            >
              {doc.mimeType.startsWith("image/") ? (
                <ImageIcon size={18} className="shrink-0 text-accent-light" />
              ) : (
                <FileText size={18} className="shrink-0 text-accent-light" />
              )}
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-sm font-medium hover:text-accent-light"
              >
                {doc.filename}
              </a>
              <span className="text-xs text-text-tertiary">
                {format(new Date(doc.uploadedAt), "d MMM yyyy")}
              </span>
              <Button
                variant="ghost"
                size="icon"
                title="Delete document"
                onClick={() => {
                  if (confirm(`Delete ${doc.filename}?`)) remove.mutate(doc.id);
                }}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
